/**
 * The ONE place an instant (a moment the API sends: `createdAt`, an audit time, a scheduled
 * charge) is rendered as a date or a time a person reads (DNKADM-0476 F-05). The console
 * shows every such moment on the venue's clock: the location's zone, else the tenant's, else
 * the device ({@link useCalendarZone} resolves it; `null` here means "the device"). A bare
 * `toLocaleString` / `toLocaleDateString` / `toLocaleTimeString` or a zone-less
 * `Intl.DateTimeFormat` renders in the BROWSER's zone, so a desk in another zone (or a
 * travelling admin) saw a different day and hour than the venue. The ESLint guard in
 * `eslint.config.js` keeps new code on this module.
 *
 * Two families stay elsewhere on purpose:
 *  - a `YYYY-MM-DD` calendar day is shown from its digits (`formatApiDate` in
 *    `@/components/list/listDate`, `dateLabel`/`shortDateLabel` in `@/utils/clockTime`), never
 *    parsed into an instant;
 *  - a 12-hour wall clock for the reservation surfaces is `clockInZone` / `isoClockAsIs`
 *    (`@/utils/clockTime`).
 *
 * The locale stays the operator's (`undefined`), as before: only the ZONE moves to the venue.
 * An unusable zone id degrades to the device zone, never throws.
 */

/** `Jun 10, 9:12 AM`: the default row timestamp (audit logs, ledgers, activity). */
export const DATE_TIME = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
} as const satisfies Intl.DateTimeFormatOptions;

/** `Jun 10, 2026, 9:12 AM`. */
export const DATE_TIME_YEAR = { ...DATE_TIME, year: 'numeric' } as const satisfies Intl.DateTimeFormatOptions;

/** `Jun 10, 2026`. */
export const DATE_ONLY = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
} as const satisfies Intl.DateTimeFormatOptions;

/** `9:12 AM`. */
export const TIME_ONLY = { hour: 'numeric', minute: '2-digit' } as const satisfies Intl.DateTimeFormatOptions;

/**
 * The epoch ms of an ISO-8601 instant or an epoch string/number, or null when absent or
 * unparseable.
 */
export function instantMs(ts: string | number | Date | null | undefined): number | null {
  if (ts === null || ts === undefined) return null;
  if (ts instanceof Date) return Number.isFinite(ts.getTime()) ? ts.getTime() : null;
  if (typeof ts === 'number') return Number.isFinite(ts) ? ts : null;
  if (ts.trim() === '') return null;
  const parsed = Date.parse(ts);
  if (Number.isFinite(parsed)) return parsed;
  const epoch = Number(ts);
  return Number.isFinite(epoch) ? epoch : null;
}

/**
 * Epoch ms rendered with `options` in `timeZone` (an IANA id; `null` or unusable: the device
 * zone). Never throws.
 */
export function formatInstantMs(
  ms: number,
  timeZone: string | null,
  options: Intl.DateTimeFormatOptions = DATE_TIME,
): string {
  const date = new Date(ms);
  if (timeZone) {
    try {
      return date.toLocaleString(undefined, { ...options, timeZone });
    } catch {
      // An IANA id the runtime rejects degrades to the device zone below.
    }
  }
  return date.toLocaleString(undefined, options);
}

/**
 * An instant for display in `timeZone` (see {@link formatInstantMs}): `placeholder` when
 * absent, the raw value when it does not parse. Never throws.
 */
export function formatInstant(
  ts: string | number | null | undefined,
  timeZone: string | null,
  options: Intl.DateTimeFormatOptions = DATE_TIME,
  placeholder = '—',
): string {
  if (ts === null || ts === undefined) return placeholder;
  const ms = instantMs(ts);
  if (ms === null) return typeof ts === 'string' && ts.trim() !== '' ? ts : placeholder;
  return formatInstantMs(ms, timeZone, options);
}
